"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { useUpdateActiveRole } from "@/hooks/auth/useUpdateActiveRole";

interface RoleSwitchContextValue {
  updateRole: (role: string) => Promise<void>;
  isSwitching: boolean;
}

const RoleSwitchContext = createContext<RoleSwitchContextValue | null>(null);

interface RoleSwitchProviderProps {
  children: ReactNode;
  /** Current active role from server; overlay is cleared when this matches the role we switched to. */
  initialActiveRole?: string | null;
}

export function RoleSwitchProvider({ children, initialActiveRole }: RoleSwitchProviderProps) {
  const router = useRouter();
  const { updateRole: updateRoleFromHook } = useUpdateActiveRole();
  const [isSwitching, setIsSwitching] = useState(false);
  const targetRoleRef = useRef<string | null>(null);

  const updateRole = useCallback(
    async (role: string) => {
      targetRoleRef.current = role;
      setIsSwitching(true);
      try {
        await updateRoleFromHook(role);
        // Always redirect to dashboard on role switch so user is never left on an admin-only page as Teacher/Staff
        router.replace("/admin/dashboard");
        router.refresh();
        // Don't set isSwitching false here – wait for server to re-render with new role (see useEffect below)
      } catch {
        targetRoleRef.current = null;
        setIsSwitching(false);
      }
    },
    [updateRoleFromHook, router],
  );

  // When server sends new initialActiveRole after refresh, clear overlay once it matches the role we switched to
  useEffect(() => {
    const target = targetRoleRef.current;
    if (!target || !isSwitching) return;
    if (initialActiveRole === target) {
      targetRoleRef.current = null;
      setIsSwitching(false);
    }
  }, [initialActiveRole, isSwitching]);

  return (
    <RoleSwitchContext.Provider value={{ updateRole, isSwitching }}>
      {children}
    </RoleSwitchContext.Provider>
  );
}

export function useRoleSwitch(): RoleSwitchContextValue | null {
  return useContext(RoleSwitchContext);
}
